import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { campaignApi, agentApi } from "../../api/client";
import { Button, Badge, Input, Card, Modal } from "../../components/ui";
import ChannelIcon from "../../components/ChannelIcon";
import PageHeader from "../../components/PageHeader";
import CampaignVoiceBlock from "./voice/CampaignVoiceBlock";
import RecipientListPreview from "./voice/RecipientListPreview";
import CampaignLaunchConfirmationModal from "./voice/CampaignLaunchConfirmationModal";
import CampaignDispositionDashboard from "./voice/CampaignDispositionDashboard";
import { outboundVoiceApi } from "../../api/client";

const STATUS_COLORS = { draft: COLORS.textMuted, scheduled: COLORS.orange, running: COLORS.accent, paused: COLORS.orange, completed: COLORS.green, failed: COLORS.pink };
const STATUS_ICONS = { draft: "\u270E", scheduled: "\u23F0", running: "\u25B6", paused: "\u23F8", completed: "\u2714", failed: "\u2716" };

// Voice-channel fields land at the API's top level (per the 2026-06-02 "B5
// merge"). Anything in this list gets stripped from the config blob so we don't
// double-send. Exported for unit testing.
export const VOICE_TOP_LEVEL_KEYS = [
  "voice_trunk_id",
  "voice_first_message",
  "voice_amd_strategy",
  "voice_voicemail_action",
  "voice_voicemail_script",
  "voice_ring_timeout_seconds",
  "voice_max_call_duration_seconds",
  "voice_max_concurrent_calls",
  "voice_retry",
  "voice_recording_consent_disclosed",
  "voice_daily_cap_usd",
  "voice_quiet_hours_start",
  "voice_quiet_hours_end",
];

// Split a config blob into { voiceTopLevel, configWithoutVoice }. Empty / null /
// undefined voice values are dropped from voiceTopLevel (so we don't send blanks)
// but always removed from configWithoutVoice. Exported for unit testing.
export function splitVoicePayloadKeys(config) {
  const cfg = config || {};
  const voiceTopLevel = {};
  const configWithoutVoice = { ...cfg };
  for (const key of VOICE_TOP_LEVEL_KEYS) {
    if (cfg[key] !== undefined && cfg[key] !== null && cfg[key] !== "") {
      voiceTopLevel[key] = cfg[key];
    }
    delete configWithoutVoice[key];
  }
  return { voiceTopLevel, configWithoutVoice };
}

// On channel switch, drop config keys prefixed for OTHER channels so a template
// designed for one channel can't leak into another. Exported for unit testing.
export function dropOtherChannelConfig(config, channel) {
  const cfg = { ...(config || {}) };
  const dropByPrefix = { whatsapp: "whatsapp_", sms: "sms_", email: "email_", voice: "voice_" };
  for (const otherCh of Object.keys(dropByPrefix)) {
    if (otherCh === channel) continue;
    const prefix = dropByPrefix[otherCh];
    for (const k of Object.keys(cfg)) {
      if (k.startsWith(prefix)) delete cfg[k];
    }
  }
  return cfg;
}

// Compute the list of unmet launch preconditions for the create form. Drives
// both the inline "missing" hint and the button's disabled/tooltip state.
// Exported for unit testing.
export function computeUnmetPreconditions(form, editingId) {
  const unmet = [];
  if (!form.name.trim()) unmet.push("Campaign name");
  if (!form.recipients_text.trim()) unmet.push("Recipient list");
  if (form.channel === "voice" && !editingId) {
    const cfg = form.config || {};
    if (!cfg.voice_trunk_id) unmet.push("Voice trunk");
    if (!cfg.voice_agent_id) unmet.push("Voice agent");
    if (!cfg.voice_recording_consent_disclosed) unmet.push("Recording disclosure acknowledgement");
  }
  return unmet;
}

// Parse a CSV file into newline-joined "handle, name" rows that the
// existing recipients_text textarea understands.
//
// Accepts:
//   - delimiter = "," or ";" or "\t" (auto-detected from header line)
//   - optional header row whose columns include any of:
//       email, e-mail, mail, address, handle, phone, contact, recipient
//       name, full name, contact name, recipient name
//     (case-insensitive). If header missing → assumes column 0 is the
//     handle (email/phone) and column 1 (if present) is the name.
//   - quoted fields per RFC 4180 (double-quote escaping).
//
// Returns: { rows: [{handle,name}], errors: [string] }.
// Exported for unit testing.
export function parseCsvText(text) {
  const errors = [];
  const rows = [];
  if (!text || !text.trim()) return { rows, errors };

  // Split lines, preserving content; drop empty lines.
  const raw = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter(l => l.length > 0);
  if (raw.length === 0) return { rows, errors };

  // Auto-detect delimiter from first line — pick the candidate with
  // the highest count, biased toward comma if tied.
  const candidates = [",", ";", "\t"];
  let delim = ",";
  let bestCount = 0;
  for (const d of candidates) {
    const c = (raw[0].match(new RegExp("\\" + d, "g")) || []).length;
    if (c > bestCount) { bestCount = c; delim = d; }
  }

  // RFC 4180-ish field tokenizer. Handles "quoted, fields" + "" escapes.
  const tokenize = (line) => {
    const out = [];
    let cur = "";
    let inQ = false;
    let i = 0;
    while (i < line.length) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"') {
          if (line[i + 1] === '"') { cur += '"'; i += 2; continue; }
          inQ = false; i++; continue;
        }
        cur += ch; i++; continue;
      }
      if (ch === '"') { inQ = true; i++; continue; }
      if (ch === delim) { out.push(cur); cur = ""; i++; continue; }
      cur += ch; i++;
    }
    out.push(cur);
    return out.map(s => s.trim());
  };

  // Decide if first line is a header. Header recognised by ANY column
  // matching one of our keywords (case-insensitive, no @ sign).
  const HANDLE_KEYS = new Set(["email", "e-mail", "mail", "address", "handle", "phone", "contact", "recipient"]);
  const NAME_KEYS = new Set(["name", "full name", "contact name", "recipient name", "first name"]);
  const firstCols = tokenize(raw[0]).map(c => c.toLowerCase());
  const headerHit = firstCols.some(c => HANDLE_KEYS.has(c) || NAME_KEYS.has(c));

  let handleIdx = 0;
  let nameIdx = 1;
  let dataStart = 0;
  if (headerHit) {
    handleIdx = firstCols.findIndex(c => HANDLE_KEYS.has(c));
    nameIdx = firstCols.findIndex(c => NAME_KEYS.has(c));
    if (handleIdx < 0) handleIdx = 0;
    dataStart = 1;
  } else {
    // Auto-detect by content of first row: column with @ or +digit is handle
    if (firstCols[0] && !/[@+0-9]/.test(firstCols[0]) && firstCols[1] && /[@+0-9]/.test(firstCols[1])) {
      handleIdx = 1; nameIdx = 0;
    }
  }

  for (let r = dataStart; r < raw.length; r++) {
    const cells = tokenize(raw[r]);
    const handle = (cells[handleIdx] || "").trim();
    const name = nameIdx >= 0 ? (cells[nameIdx] || "").trim() : "";
    if (!handle) {
      errors.push(`Row ${r + 1}: empty handle, skipped`);
      continue;
    }
    rows.push({ handle, name });
  }
  return { rows, errors };
}

// Parse the recipients textarea into [{handle, name}] — one recipient per
// line. Accepts "handle, name" (comma-split), "email@x.com Full Name"
// (email + trailing name), or a bare handle. Empty lines are dropped and a
// missing name is normalized to null. Extracted from the create-form closure
// so D1's voice preview + launch-confirm can reuse it. Exported for unit testing.
export function parseRecipientsText(text) {
  return (text || "").trim().split("\n").filter(Boolean).map(line => {
    const trimmed = line.trim();
    if (trimmed.includes(",")) {
      const [handle, name] = trimmed.split(",", 2).map(s => s.trim());
      return { handle, name: name || null };
    }
    const emailMatch = trimmed.match(/^(\S+@\S+)\s+(.+)$/);
    if (emailMatch) {
      return { handle: emailMatch[1], name: emailMatch[2].trim() };
    }
    return { handle: trimmed, name: null };
  });
}

const CampaignsView = () => {
  const bp = useBreakpoint();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [agents, setAgents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailMessages, setDetailMessages] = useState([]);
  const [triggering, setTriggering] = useState(null);
  const [saving, setSaving] = useState(false);
  const [csvImportNote, setCsvImportNote] = useState(null);
  const csvFileRef = useRef(null);

  // D1 — voice channel pre-dispatch validation + confirmation gate.
  // Lives in CampaignsView (not the voice block) because the modal
  // gates the existing handleCreate flow.
  const [voiceValidation, setVoiceValidation] = useState(null);
  const [voiceTrunks, setVoiceTrunks] = useState([]);
  const [showLaunchConfirm, setShowLaunchConfirm] = useState(false);
  const [pendingVoicePayload, setPendingVoicePayload] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    name: "", description: "", campaign_type: "one_time", channel: "whatsapp",
    message_template: "", use_agent: false, agent_id: "",
    recipients_text: "", rate_limit: 10, batch_size: 50,
    config: {},
  });

  const fetchCampaigns = async ({ silent = false } = {}) => {
    // ``silent=true`` skips the loading-skeleton flash for background
    // polls. The user-action path (initial load + filter change)
    // still shows the skeleton so the user knows something fired.
    if (!silent) setLoading(true);
    try {
      const res = await campaignApi.list(filter === "all" ? null : filter);
      if (res.success) setCampaigns(res.data || []);
    } catch {} finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, [filter]);
  useEffect(() => {
    agentApi.list().then(res => { if (res.success) setAgents(res.data || []); }).catch(() => {});
    // Cache trunks once on mount so the launch-confirm modal can resolve
    // trunk identity (caller-ID, cap) without a second roundtrip.
    outboundVoiceApi.listTrunks().then(res => { setVoiceTrunks(res?.data || res || []); }).catch(() => {});
  }, []);

  // Bug #2 fix (2026-05-14) — periodic poll while any campaign is
  // in-flight on the server. Pre-fix the list refetched only on
  // user action; a campaign transitioning running → completed on
  // the server was invisible until the user manually refreshed,
  // and the action buttons keyed on terminal status ("Reset to
  // Draft", "Edit", "Delete") stayed hidden.
  //
  // 5s cadence matches IngestionView + KnowledgeJobsPanel + the
  // platform's other polling surfaces. Effect only runs when at
  // least one campaign in the current list is in an active state,
  // so a tenant with only-finished campaigns burns no API calls.
  // ``silent=true`` avoids the loading-skeleton flash every tick.
  useEffect(() => {
    const ACTIVE = new Set(["running", "scheduled", "pending"]);
    const hasActive = campaigns.some(c => ACTIVE.has(c.status));
    if (!hasActive) return;
    const interval = setInterval(() => fetchCampaigns({ silent: true }), 5000);
    return () => clearInterval(interval);
  }, [campaigns]);

  // Parse recipients_text into [{handle, name}]. Extracted so D1's
  // voice-validation preview + launch-confirm can call it without
  // going through handleCreate.
  const parseRecipientsFromForm = () => parseRecipientsText(form.recipients_text);

  const handleCreate = async () => {
    // Parse recipients from textarea (one per line)
    // Supports: "email@example.com, Name" or "email@example.com Name" or just "email@example.com"
    const recipients = parseRecipientsFromForm();

    // Voice-channel fields land at the API's top level (per the
    // 2026-06-02 "B5 merge" — CampaignCreate now exposes these as
    // first-class fields, not nested under `config`). Strip them from
    // the config blob so we don't double-send. Other channels (email,
    // sms, whatsapp) continue to use `config` for their per-channel
    // settings (whatsapp_template_name, email_subject, …).
    const cfg = form.config || {};
    const { voiceTopLevel, configWithoutVoice } = splitVoicePayloadKeys(cfg);

    const payload = {
      name: form.name,
      description: form.description,
      campaign_type: form.campaign_type,
      channel: form.channel,
      message_template: form.message_template,
      use_agent: form.use_agent,
      agent_id: form.use_agent ? form.agent_id : null,
      recipients: recipients.length > 0 ? recipients : null,
      rate_limit: form.rate_limit,
      batch_size: form.batch_size,
      config: Object.keys(configWithoutVoice).length > 0 ? configWithoutVoice : null,
      ...voiceTopLevel,
    };

    // D1 — voice channels route through the launch-confirm gate
    // first. The actual POST happens in handleConfirmedVoiceLaunch
    // after the operator clicks "Launch" in the modal.
    if (form.channel === "voice" && !editingId) {
      if (!cfg.voice_trunk_id) {
        setTriggerError("Select a voice trunk before launching.");
        return;
      }
      if (!cfg.voice_agent_id) {
        setTriggerError("Select a voice agent before launching.");
        return;
      }
      if (!cfg.voice_recording_consent_disclosed) {
        setTriggerError("Acknowledge the recording disclosure before launching.");
        return;
      }
      // Voice campaigns always use the agent path (per runbook
      // convention — voice agents are authored specifically for
      // outbound). Override use_agent + agent_id from the voice
      // block selection.
      payload.use_agent = true;
      payload.agent_id = cfg.voice_agent_id;
      setPendingVoicePayload(payload);
      setShowLaunchConfirm(true);
      return;
    }

    setSaving(true);
    try {
      let res;
      if (editingId) {
        try {
          res = await campaignApi.update(editingId, payload);
        } catch (err) {
          // Backend rejects updates on terminal-state campaigns. The UI
          // can render a stale "draft" status (e.g. after navigation
          // away + back) — auto-reset and retry once so the user
          // doesn't have to manually click "Reset to Draft" first.
          const msg = (err?.message || "").toLowerCase();
          if (msg.includes("can only edit draft")) {
            await campaignApi.reset(editingId);
            res = await campaignApi.update(editingId, payload);
          } else {
            throw err;
          }
        }
      } else {
        res = await campaignApi.create(payload);
      }
      if (res.success) {
        setShowCreate(false);
        setEditingId(null);
        setForm({ name: "", description: "", campaign_type: "one_time", channel: "whatsapp", message_template: "", use_agent: false, agent_id: "", recipients_text: "", rate_limit: 10, batch_size: 50, config: {} });
        fetchCampaigns();
      }
    } catch (err) {
      setTriggerError(err.message || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const [triggerError, setTriggerError] = useState(null);

  // D1 — confirmed voice launch: POST create + immediately trigger.
  // Mirrors the non-voice success path but also kicks off /trigger
  // so the operator gets one-click dispatch instead of two.
  const handleConfirmedVoiceLaunch = async () => {
    if (!pendingVoicePayload) return;
    setSaving(true);
    try {
      const res = await campaignApi.create(pendingVoicePayload);
      if (res?.success && res.data?.id) {
        try {
          await campaignApi.trigger(res.data.id);
        } catch (err) {
          // Surface dispatch failure but don't roll back the campaign
          // — operator can re-trigger from the list view.
          setTriggerError(err.message || "Campaign created but dispatch failed; trigger manually from the list.");
        }
      }
      setShowLaunchConfirm(false);
      setShowCreate(false);
      setEditingId(null);
      setPendingVoicePayload(null);
      setVoiceValidation(null);
      setForm({ name: "", description: "", campaign_type: "one_time", channel: "whatsapp", message_template: "", use_agent: false, agent_id: "", recipients_text: "", rate_limit: 10, batch_size: 50, config: {} });
      fetchCampaigns();
    } catch (err) {
      setTriggerError(err.message || "Failed to launch campaign");
      setShowLaunchConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  // D12 — clone a completed/paused campaign by pre-filling the create
  // modal with its config (name suffixed " (copy)", recipients
  // dropped so the operator re-selects).
  const handleClone = async (c) => {
    try {
      const res = await campaignApi.clone(c.id);
      const src = res?.data || res || c;
      setForm({
        name: `${src.name || "Campaign"} (copy)`,
        description: src.description || "",
        campaign_type: src.campaign_type || "one_time",
        channel: src.channel || "whatsapp",
        message_template: src.message_template || "",
        use_agent: src.use_agent || false,
        agent_id: src.agent_id || "",
        recipients_text: "",
        rate_limit: src.rate_limit || 10,
        batch_size: src.batch_size || 50,
        config: src.campaign_config || src.config || {},
      });
      setEditingId(null);
      setShowCreate(true);
    } catch (err) {
      setTriggerError(err.message || "Failed to clone campaign");
    }
  };

  const handleTrigger = async (id) => {
    setTriggering(id);
    setTriggerError(null);
    try {
      await campaignApi.trigger(id);
      setTimeout(fetchCampaigns, 1000);
    } catch (err) {
      setTriggerError(err.message || "Failed to trigger campaign");
    } finally { setTriggering(null); }
  };

  const handleEdit = async (c) => {
    // Backend only allows updates on draft / paused. For terminal-state
    // campaigns (completed / failed / completed_with_errors), reset
    // back to draft first so the operator can tweak and re-trigger
    // without manually resetting in two clicks.
    let working = c;
    const TERMINAL = new Set(["completed", "failed", "completed_with_errors", "cancelled"]);
    if (TERMINAL.has(c.status)) {
      try {
        const res = await campaignApi.reset(c.id);
        if (res?.success && res.data) {
          working = { ...c, ...res.data, status: "draft" };
        } else {
          working = { ...c, status: "draft" };
        }
      } catch (err) {
        setTriggerError(err.message || "Could not reset campaign before edit");
        return;
      }
    }

    setEditingId(working.id);
    // Voice fields are now top-level on the API response (PR #236
    // B5 merge). The UI's form.config shape is unchanged (the voice
    // block binds to `form.config.voice_*`), so we synthesize the
    // top-level voice fields back into the config object on load.
    // The reverse — config.voice_* → payload.voice_* top-level — runs
    // on save in handleCreate.
    const loadedConfig = { ...(working.campaign_config || working.config || {}) };
    for (const key of [
      "voice_trunk_id",
      "voice_first_message",
      "voice_amd_strategy",
      "voice_voicemail_action",
      "voice_voicemail_script",
      "voice_ring_timeout_seconds",
      "voice_max_call_duration_seconds",
      "voice_max_concurrent_calls",
      "voice_retry",
    ]) {
      if (working[key] !== undefined && working[key] !== null) {
        loadedConfig[key] = working[key];
      }
    }
    setForm({
      name: working.name || "",
      description: working.description || "",
      campaign_type: working.campaign_type || "one_time",
      channel: working.channel || "whatsapp",
      message_template: working.message_template || "",
      use_agent: working.use_agent || false,
      agent_id: working.agent_id || "",
      recipients_text: (working.recipients || []).map(r => r.name ? `${r.handle}, ${r.name}` : r.handle).join("\n"),
      rate_limit: working.rate_limit || 10,
      batch_size: working.batch_size || 50,
      config: loadedConfig,
    });
    setShowCreate(true);
    // The reset above flipped status server-side; refresh the list so the
    // card reflects "draft" instead of "completed".
    fetchCampaigns();
  };

  const handleReset = async (id) => {
    try {
      await campaignApi.reset(id);
      fetchCampaigns();
    } catch (err) {
      setTriggerError(err.message || "Failed to reset campaign");
    }
  };

  const handleDelete = async (id) => {
    try {
      await campaignApi.delete(id);
      fetchCampaigns();
    } catch {}
  };

  const openDetail = async (id) => {
    setShowDetail(id);
    try {
      const [statsRes, msgsRes] = await Promise.all([campaignApi.get(id), campaignApi.messages(id)]);
      if (statsRes.success) setDetailData(statsRes.data);
      if (msgsRes.success) setDetailMessages(msgsRes.data || []);
    } catch {}
  };

  return (
    <div className="panel-scroll" style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
      <PageHeader
        title="Campaigns"
        subtitle="Proactive messaging across WhatsApp, SMS, Email, and Voice"
        primaryAction={{
          label: "New Campaign",
          iconName: "action.add",
          onClick: () => setShowCreate(true),
          style: { background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`, border: "none", color: "#fff" },
        }}
        filters={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["all", "draft", "scheduled", "running", "paused", "completed", "failed"].map(f => (
              <Button key={f} size="sm" variant={filter === f ? "primary" : "secondary"}
                onClick={() => setFilter(f)}
                style={filter === f ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}` } : {}}>
                {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>
        }
      />

      {/* Trigger error banner */}
      {triggerError && (
        <div style={{ padding: "12px 16px", marginBottom: 16, borderRadius: 8, background: `${COLORS.pink}12`, border: `1px solid ${COLORS.pink}30`, color: COLORS.pink, fontSize: 13 }}>
          <strong>Cannot send:</strong> {triggerError}
          <button onClick={() => setTriggerError(null)} style={{ float: "right", background: "none", border: "none", color: COLORS.pink, cursor: "pointer", fontSize: 16 }}>{"\u00D7"}</button>
        </div>
      )}

      {/* Campaign list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted }}>Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: COLORS.textDim }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{"\uD83D\uDCE2"}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textMuted }}>No campaigns yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Create your first outbound campaign to reach customers proactively</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(auto-fill, minmax(340, 1fr))", gap: 14 }}>
          {campaigns.map((c, idx) => (
            // i2-visual-identity-v2 §10.2 B2 — Campaigns row card.
            // spatialShadow + RADIUS.inner + cascade entry + spring hover.
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring", stiffness: 400, damping: 25,
                delay: Math.min(idx * 0.05, 0.4),
              }}
              whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
              onClick={() => openDetail(c.id)}
              style={{
                padding: 18,
                borderRadius: RADIUS.inner,
                background: COLORS.surface,
                border: `1px solid ${STATUS_COLORS[c.status]}25`,
                boxShadow: MATERIALS.spatialShadow(),
                cursor: "pointer",
              }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ChannelIcon channel={c.channel} size={18} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{c.campaign_type} {"\u00B7"} {c.channel}</div>
                  </div>
                </div>
                <Badge color={STATUS_COLORS[c.status]}>
                  {STATUS_ICONS[c.status]} {c.status}
                </Badge>
              </div>

              {/* Stats row */}
              <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>Recipients</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>{c.total_recipients}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>Sent</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.green }}>{c.sent_count}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>Failed</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.pink }}>{c.failed_count}</div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(c.status === "draft" || c.status === "paused") && (
                  <Button size="sm" onClick={e => { e.stopPropagation(); handleTrigger(c.id); }}
                    disabled={triggering === c.id}
                    style={{ background: `${COLORS.green}15`, color: COLORS.green, border: `1px solid ${COLORS.green}30` }}>
                    {triggering === c.id ? "..." : "\u25B6 Send"}
                  </Button>
                )}
                {(c.status === "failed" || c.status === "completed" || c.status === "completed_with_errors") && (
                  <Button size="sm" onClick={e => { e.stopPropagation(); handleReset(c.id); }}
                    style={{ background: `${COLORS.orange}15`, color: COLORS.orange, border: `1px solid ${COLORS.orange}30` }}>
                    {"\u21BA"} Reset to Draft
                  </Button>
                )}
                {c.status !== "running" && c.status !== "scheduled" && (
                  <Button size="sm" variant="secondary" onClick={e => { e.stopPropagation(); handleEdit(c); }}
                    style={{ color: COLORS.accentText }}>
                    {"\u270E"} Edit
                  </Button>
                )}
                {(c.status === "completed" || c.status === "completed_with_errors" || c.status === "paused" || c.status === "failed") && (
                  <Button size="sm" variant="secondary" onClick={e => { e.stopPropagation(); handleClone(c); }}
                    style={{ color: COLORS.accentText }}
                    title="Create a new draft pre-filled from this campaign's config">
                    {"⧉"} Duplicate
                  </Button>
                )}
                {(c.status === "draft" || c.status === "completed" || c.status === "failed" || c.status === "completed_with_errors" || c.status === "cancelled") && (
                  <Button size="sm" variant="secondary" onClick={e => { e.stopPropagation(); handleDelete(c.id); }}
                    style={{ color: COLORS.pink }}>
                    Delete
                  </Button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Campaign Modal */}
      <Modal open={showCreate} title={editingId ? "Edit Campaign" : "New Outbound Campaign"} onClose={() => { setShowCreate(false); setEditingId(null); setForm({ name: "", description: "", campaign_type: "one_time", channel: "whatsapp", message_template: "", use_agent: false, agent_id: "", recipients_text: "", rate_limit: 10, batch_size: 50, config: {} }); }}>
          <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "0 4px" }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Campaign Name</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Black Friday Promo"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 13 }} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Channel</label>
              <div style={{ display: "flex", gap: 8 }}>
                {["whatsapp", "sms", "email", "voice"].map(ch => (
                  <Button key={ch} size="sm" variant={form.channel === ch ? "primary" : "secondary"}
                    onClick={() => setForm(f => {
                      if (f.channel === ch) return f;
                      // Reset channel-specific config keys on channel switch.
                      // Without this, fields entered for a prior channel (e.g.
                      // whatsapp_template_name) ride along on the PUT, and a
                      // template designed for one channel can leak into another.
                      return { ...f, channel: ch, config: dropOtherChannelConfig(f.config, ch) };
                    })}
                    style={form.channel === ch ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}` } : {}}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <ChannelIcon channel={ch} size={14} /> {ch}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Generic "AI agent generates message text" toggle —
                relevant for whatsapp / sms / email channels where the
                agent composes a static message string. Voice channels
                have their own "Voice agent" picker inside the
                CampaignVoiceBlock that handles the live conversation;
                showing this block too is confusing UX (the save-time
                code at handleCreate silently overrides agent_id from
                voice_agent_id anyway). Hide for voice. */}
            {form.channel !== "voice" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.use_agent} onChange={e => setForm(f => ({ ...f, use_agent: e.target.checked }))} />
                  <span style={{ fontSize: 12, color: COLORS.text }}>Use AI agent to generate personalized messages</span>
                </label>
              </div>
            )}

            {form.channel !== "voice" && form.use_agent ? (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Agent</label>
                <select value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))}
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }}>
                  <option value="">Select an agent...</option>
                  {agents
                    .filter(a => a.status !== "archived")
                    .map(a => {
                      const s = a.status || "live";
                      const isLive = s === "live" || s === "active";
                      return (
                        <option key={a.id} value={a.id}>
                          {a.name}{!isLive ? ` (${s})` : ""}
                        </option>
                      );
                    })}
                </select>
                {agents.filter(a => a.status !== "archived").length === 0 && (
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
                    No agents available. Create one in the Library tab first.
                  </div>
                )}
              </div>
            ) : null}

            {/* The static-text "Message Template" / "Prompt for agent"
                textarea is for text channels — whatsapp/sms/email
                consume a string-shaped message. Voice campaigns set
                their opening line via voice_first_message in the
                CampaignVoiceBlock (and the rest of the conversation
                is driven by the voice agent's runtime prompt). Hide
                for voice. */}
            {form.channel !== "voice" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>
                  {form.use_agent ? "Prompt for agent (optional context)" : "Message Template"}
                </label>
                <textarea value={form.message_template} onChange={e => setForm(f => ({ ...f, message_template: e.target.value }))}
                  placeholder={form.use_agent
                    ? "e.g. Remind this customer about their upcoming appointment on Friday"
                    : "Hi {{name}}, we have an exclusive offer for you!"}
                  rows={3}
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12, fontFamily: "inherit", resize: "vertical" }} />
                {!form.use_agent && (
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Variables: {"{{name}}"}, {"{{handle}}"}</div>
                )}
              </div>
            )}

            {form.channel === "whatsapp" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>WhatsApp Template Name (optional)</label>
                <input type="text" value={form.config.whatsapp_template_name || ""}
                  onChange={e => setForm(f => ({ ...f, config: { ...f.config, whatsapp_template_name: e.target.value } }))}
                  placeholder="e.g. appointment_reminder"
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>Required for outbound WhatsApp messages outside 24h window</div>
              </div>
            )}

            {form.channel === "voice" && (
              <CampaignVoiceBlock
                value={form.config}
                onChange={(cfg) => setForm(f => ({ ...f, config: cfg }))}
                agents={agents}
              />
            )}

            {form.channel === "email" && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Email Subject</label>
                  <input type="text" value={form.config.email_subject || ""}
                    onChange={e => setForm(f => ({ ...f, config: { ...f.config, email_subject: e.target.value } }))}
                    placeholder="e.g. Your exclusive offer awaits!"
                    style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                </div>

                <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>From Name (optional)</label>
                    <input type="text" value={form.config.email_from_name || ""}
                      onChange={e => setForm(f => ({ ...f, config: { ...f.config, email_from_name: e.target.value } }))}
                      placeholder="e.g. Customer Success Team"
                      style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>From Email (optional)</label>
                    <input type="email" value={form.config.email_from || ""}
                      onChange={e => setForm(f => ({ ...f, config: { ...f.config, email_from: e.target.value } }))}
                      placeholder="defaults to tenant SMTP"
                      style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
                  </div>
                </div>
                <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: -8, marginBottom: 16 }}>
                  Visible From header on the email. Replies route here. Envelope sender stays on the tenant's verified SMTP address so unverified-sender rejects don't break the whole batch.
                </div>
              </>
            )}

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted }}>
                  Recipients
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    ref={csvFileRef}
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        const { rows, errors } = parseCsvText(text);
                        if (rows.length === 0) {
                          setCsvImportNote({
                            kind: "error",
                            text: `No usable rows in ${file.name}.${errors.length ? ` First issue: ${errors[0]}` : ""}`,
                          });
                          return;
                        }
                        const lines = rows.map(r => r.name ? `${r.handle}, ${r.name}` : r.handle).join("\n");
                        const existing = form.recipients_text.trim();
                        // Append to existing, dedup by handle (keeps first
                        // occurrence). Lets the user import multiple CSVs.
                        const merged = (existing ? existing + "\n" + lines : lines)
                          .split("\n")
                          .map(l => l.trim())
                          .filter(Boolean);
                        const seen = new Set();
                        const unique = [];
                        for (const line of merged) {
                          const handle = (line.split(",")[0] || "").trim().toLowerCase();
                          if (!handle || seen.has(handle)) continue;
                          seen.add(handle);
                          unique.push(line);
                        }
                        setForm(f => ({ ...f, recipients_text: unique.join("\n") }));
                        setCsvImportNote({
                          kind: "ok",
                          text: `Imported ${rows.length} from ${file.name}${errors.length ? ` (${errors.length} skipped)` : ""}. Total: ${unique.length}.`,
                        });
                      } catch (err) {
                        setCsvImportNote({ kind: "error", text: `CSV read failed: ${err.message || err}` });
                      } finally {
                        // Allow re-importing the same file later
                        if (csvFileRef.current) csvFileRef.current.value = "";
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => csvFileRef.current?.click()}
                    style={{ fontSize: 11, padding: "4px 10px" }}
                  >
                    {"\u2B07"} Import CSV
                  </Button>
                  {form.recipients_text.trim().length > 0 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => { setForm(f => ({ ...f, recipients_text: "" })); setCsvImportNote(null); }}
                      style={{ fontSize: 11, padding: "4px 10px", color: COLORS.pink }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <textarea value={form.recipients_text} onChange={e => setForm(f => ({ ...f, recipients_text: e.target.value }))}
                placeholder={form.channel === "email"
                  ? "john@example.com, John Doe\njane@example.com, Jane Smith\n\n— or click \"Import CSV\" above —"
                  : "+15551234567, John Doe\n+15559876543, Jane Smith\n\n— or click \"Import CSV\" above —"}
                rows={5}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12, fontFamily: "monospace", resize: "vertical" }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4, fontSize: 10 }}>
                <div style={{ color: COLORS.textDim }}>
                  {form.recipients_text.trim().split("\n").filter(Boolean).length} recipient{form.recipients_text.trim().split("\n").filter(Boolean).length !== 1 ? "s" : ""}
                  {" \u00B7 "}
                  {form.channel === "voice" || form.channel === "sms"
                    ? "CSV: phone + name column (header optional)"
                    : form.channel === "email"
                      ? "CSV: email + name column (header optional)"
                      : "CSV: handle + name column (header optional)"}
                </div>
                {csvImportNote && (
                  <div style={{
                    color: csvImportNote.kind === "ok" ? COLORS.green : COLORS.pink,
                    fontWeight: 500,
                  }}>
                    {csvImportNote.text}
                  </div>
                )}
              </div>
            </div>

            {form.channel === "voice" && form.recipients_text.trim().length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <RecipientListPreview
                  recipients={parseRecipientsFromForm()}
                  trunkId={form.config?.voice_trunk_id}
                  onValidated={setVoiceValidation}
                />
              </div>
            )}

            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Rate Limit (msg/sec)</label>
                <input type="number" value={form.rate_limit} onChange={e => setForm(f => ({ ...f, rate_limit: parseInt(e.target.value) || 10 }))}
                  min={1} max={100}
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: "block", marginBottom: 4 }}>Batch Size</label>
                <input type="number" value={form.batch_size} onChange={e => setForm(f => ({ ...f, batch_size: parseInt(e.target.value) || 50 }))}
                  min={1} max={500}
                  style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text, fontSize: 12 }} />
              </div>
            </div>

            {/* D15 — Validate-launch precondition tooltip. Compute unmet
                preconditions inline so we can both (a) render them
                under the button as plain text the operator can read
                without hovering, and (b) attach them to the button's
                `title` attribute so they appear on hover even when
                disabled. Pre-fix the operator had no way to know
                what was missing until they clicked + read the inline
                error. */}
            {(() => {
              const unmet = computeUnmetPreconditions(form, editingId);
              const disabled = saving || unmet.length > 0;
              const tooltip = unmet.length > 0
                ? `Cannot launch — missing: ${unmet.join(", ")}`
                : (form.channel === "voice" ? "Review preconditions in the launch confirmation modal" : "Create the campaign");
              return (
                <>
                  <button onClick={handleCreate} disabled={disabled} title={tooltip}
                    style={{
                      width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
                      background: disabled ? COLORS.textDim : `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                      color: "#fff", fontSize: 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
                      opacity: saving ? 0.8 : 1,
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}>
                    {saving && (
                      <span style={{
                        display: "inline-block",
                        width: 14, height: 14,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }} />
                    )}
                    {saving
                      ? "Processing…"
                      : editingId
                        ? "Save Changes"
                        : form.channel === "voice"
                          ? "Validate & launch…"
                          : "Create Campaign"}
                  </button>
                  {unmet.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: COLORS.textMuted, textAlign: "center" }}>
                      Cannot launch — missing: <strong>{unmet.join(", ")}</strong>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </Modal>

      {/* Campaign Detail Modal */}
      <Modal open={!!showDetail} title={detailData?.name || "Campaign Details"} onClose={() => { setShowDetail(null); setDetailData(null); setDetailMessages([]); }}>
          {detailData ? (
            <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* D4 — Voice campaigns get the per-call disposition
                  dashboard (Pause/Stop controls + disposition mix
                  chart + per-call retry timeline + recording
                  playback) instead of the generic Message
                  Deliveries list. The dashboard's auto-refresh
                  picks up A6 disposition column + C5 actual_cost_usd
                  rollup as the dispatcher fills them in. Non-voice
                  channels (whatsapp / sms / email) continue to use
                  the original stats-card + message-list shape. */}
              {detailData.channel === "voice" ? (
                <CampaignDispositionDashboard
                  campaignId={showDetail}
                  onPaused={fetchCampaigns}
                  onResumed={fetchCampaigns}
                />
              ) : (
                <>
                  {/* Stats cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
                    {[
                      { label: "Total", value: detailData.total_recipients, color: COLORS.text },
                      { label: "Sent", value: detailData.sent, color: COLORS.green },
                      { label: "Failed", value: detailData.failed, color: COLORS.pink },
                      { label: "Delivery", value: `${detailData.delivery_rate}%`, color: COLORS.accentText },
                    ].map(s => (
                      <div key={s.label} style={{ padding: 12, borderRadius: 10, background: COLORS.surface, textAlign: "center", border: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Message deliveries */}
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>Message Deliveries</div>
                  {detailMessages.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim, fontSize: 12 }}>No messages yet — trigger the campaign to start sending</div>
                  ) : (
                    <div style={{ maxHeight: 300, overflowY: "auto" }}>
                      {detailMessages.map(m => (
                        <div key={m.id} style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "8px 12px", borderRadius: 8, marginBottom: 4,
                          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                        }}>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.text }}>{m.recipient_name || m.recipient_handle}</div>
                            <div style={{ fontSize: 10, color: COLORS.textDim }}>{m.recipient_handle}</div>
                          </div>
                          <Badge color={m.status === "sent" || m.status === "delivered" ? COLORS.green : m.status === "failed" ? COLORS.pink : COLORS.textMuted}>
                            {m.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div style={{ padding: 20, textAlign: "center", color: COLORS.textDim }}>Loading...</div>
          )}
        </Modal>

      {/* D1 — Voice launch confirmation gate */}
      <CampaignLaunchConfirmationModal
        open={showLaunchConfirm}
        onClose={() => { setShowLaunchConfirm(false); setPendingVoicePayload(null); }}
        onConfirm={handleConfirmedVoiceLaunch}
        campaignName={form.name}
        trunk={voiceTrunks.find(t => t.id === form.config?.voice_trunk_id)}
        agent={agents.find(a => a.id === form.config?.voice_agent_id)}
        validation={voiceValidation}
        dailyCapUsd={form.config?.voice_daily_cap_usd}
        submitting={saving}
      />
    </div>
  );
};

export default CampaignsView;
